import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  MuiBox,
  MuiChipWithListView,
  MuiDateField,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  StatusBadge,
  TableHeaderSearchIcon,
} from "../../../../../../../components";
import MuiEllipseLabel from "../../../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  getCustomDateFormatWithTime,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../../../helpers/types/common";
import {
  arrayOfObjectsIncludesValue,
  buildActionMenuItems,
  checkFilterMatch,
  compareDates,
  convertToUserFormat,
  convertToUserFormatWithLocalTime,
  getUniqueCountriesFromData,
  getValueOrBlank,
  isDateInRange,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
  stringIncludesArrayOfStringsValue,
} from "../../../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../redux/slices/appState/appStateSelector";
import { updateProgramCreatedDate } from "../../../../../../../redux/slices/program/programSlice";
import { ParticipantAddActivity } from "../../../../../../createProgram/components/createProgramTabs/tabs/participant/components/participantAddActivity/ParticipantAddActivity";
import { activityTimelineSiteCoreKeys } from "../../../../../../createProgram/siteCore/SiteCoreKeys";
import {
  getCoachNamesWithType,
  getInfoMessageForAssignedCoaches,
} from "../../../../../helper";
import {
  CPParticipantsResultForm,
  CPParticipantsResultFormKey,
  CPParticipantsResultRowValue,
  UniqueParticipantSearchResultFiltersTypes,
} from "../../../../../modelsAndSchemas/Models";
import { myCaseloadsSiteCoreKeys } from "../../../../../sitecore/SiteCoreKeys";
import { CPParticipantsResultsWrapper } from "./CPParticipantsResults.style";

const pageId = PAGE_IDS.COACH_PORTAL_DASHBOARD_MY_CASELOADS_RESULTS;

interface CPParticipantsResultsProps {
  rows: CPParticipantsResultRowValue[];
  emptyMessageKey: string;
}
export const CPParticipantsResults: React.FC<CPParticipantsResultsProps> = ({
  rows,
  emptyMessageKey,
}) => {
  const { useFetchLabel, fetchValueByKeyOfDropdown, fetchLabel, commonLabels } =
    useSiteCore();
  const navigate = useNavigate();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const dispatch = useDispatch();
  const { loggedInUser } = useGetLoggedInUserDetails();

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResultsData, setFilteredResultsData] = useState([...rows]);
  const [uniqueCountriesData, setUniqueCountriesData] = useState<LabelValue[]>(
    [],
  );
  const [uniqueFiltersValues, setUniqueFiltersValues] =
    useState<UniqueParticipantSearchResultFiltersTypes>(() => ({
      programStatus: [],
    }));
  const [updatedRowsData, setUpdatedRowsData] = useState<
    CPParticipantsResultRowValue[]
  >([]);
  const currentParticipantRef = useRef<CPParticipantsResultRowValue>();
  const [showActivityModal, setShowActivityModal] = useState(false);

  const {
    myCaseloadsTitle,
    participantNameTableColumn: participantNameLabel,
    programTemplateTableColumn: programTemplateLabel,
    participantStatusTableColumn: participantStatusLabel,
    nextContactDateTableColumn: nextContactDateLabel,
    upcomingSessionTableColumn: upcomingSessionLabel,
    programEndDateTableColumn: programEndDateLabel,
    assignedCoachesTableColumn: assignedCoachesLabel,
    participantEmailTableColumn: participantEmailLabel,
    participantMobileNumberTableColumn: participantMobileNumberLabel,
    projectManagerTableColumn: projectManagerLabel,
    countryTableColumn: countryLabel,
    programStartMonthYearTableColumn: programStartMonthYearLabel,
    actionTableColumn: actionLabel,
    actionTableColumnManage: manageLabel,
    actionTableColumnAddActivity: addActivityLabel,
    participantStatusList,
  } = myCaseloadsSiteCoreKeys;
  const getLabel = useFetchLabel(myCaseloadsTitle);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const { control, setValue, getValues, reset } =
    useForm<CPParticipantsResultForm>({
      mode: "all",
      defaultValues: {
        [CPParticipantsResultFormKey.participantName]: "",
        [CPParticipantsResultFormKey.programTemplateName]: "",
        [CPParticipantsResultFormKey.programStatus]: "",
        [CPParticipantsResultFormKey.nextContactDate]: "",
        [CPParticipantsResultFormKey.upcomingSession]: "",
        [CPParticipantsResultFormKey.programEndDate]: "",
        [CPParticipantsResultFormKey.coaches]: "",
        [CPParticipantsResultFormKey.participantEmail]: "",
        [CPParticipantsResultFormKey.participantMobileNumber]: "",
        [CPParticipantsResultFormKey.projectManager]: "",
        [CPParticipantsResultFormKey.countryIds]: [],
        [CPParticipantsResultFormKey.programStartDate]: "",
      },
    });
  const formValues = useWatch({ control });

  useEffect(() => {
    resetPagination();
    const rowsData = [...rows];
    const uniqueCountriesData = getUniqueCountriesFromData(rowsData);
    setUniqueCountriesData(uniqueCountriesData);
    reset();
  }, [rows]);

  const getUniqueFiltersForCPParticipants = (
    resultsData: CPParticipantsResultRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        resultsData
          .filter((resultItem) => resultItem[key]) // Filter out null or undefined values
          .map((resultItem) => resultItem[key]) // Extract values for the given key
          .reduce((accumulator, value) => accumulator.concat(value), []), // Flatten the array if values are arrays
      ),
    ).map((resultItem) => ({
      value: resultItem as string,
      label: resultItem as string,
    })); // Format as value-label pairs
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localRowsData = [...rows];

      localRowsData.forEach((data) => {
        data.coachNamesArray = getCoachNamesWithType(
          data.coaches,
          getValueOrBlank(loggedInUser?.id),
        );

        data.statusKey = data.programStatus;

        data.statusValue = fetchValueByKeyOfDropdown(
          myCaseloadsTitle,
          participantStatusList,
          data.programStatus,
        );
      });
      setUpdatedRowsData(localRowsData);
      setFilteredResultsData([...localRowsData]);

      setUniqueFiltersValues((prevFilters) => ({
        ...prevFilters,
        [CPParticipantsResultFormKey.programStatus]:
          getUniqueFiltersForCPParticipants(localRowsData, "statusValue"),
      }));
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (isNotEmpty(updatedRowsData)) {
      const {
        participantName,
        programTemplateName,
        nextContactDate,
        upcomingSession,
        countryIds,
        coaches,
        programStatus,
        participantEmail,
        participantMobileNumber,
        projectManager,
        programStartDate,
        programEndDate,
      } = getValues();

      const myData = updatedRowsData.filter((item) => {
        const programTemplateMatch = checkFilterMatch(
          programTemplateName,
          item.programTemplateName,
        );
        const isNextContactDateMatch = compareDates(
          getValueOrBlank(nextContactDate),
          getValueOrBlank(item?.nextContactDate),
          getValueOrBlank(userCountryDateFormat),
        );

        const isUpcomingSessionDateMatch = compareDates(
          getValueOrBlank(upcomingSession),
          getValueOrBlank(item?.upcomingSession),
          getValueOrBlank(userCountryDateFormat),
        );

        const isCoachNamesMatch = arrayOfObjectsIncludesValue(
          coaches,
          item.coachNamesArray,
          "label",
        );

        const participantNameMatch = checkFilterMatch(
          getValueOrBlank(participantName),
          getValueOrBlank(item.participantName),
        );

        const participantMobileNumberMatch = checkFilterMatch(
          getValueOrBlank(participantMobileNumber),
          getValueOrBlank(item.participantMobileNumber),
        );

        const participantEmailMatch = checkFilterMatch(
          getValueOrBlank(participantEmail),
          getValueOrBlank(item.participantEmail),
        );

        const projectManagerMatch = checkFilterMatch(
          getValueOrBlank(projectManager),
          getValueOrBlank(item.projectManager),
        );

        const isCountryMatch = stringIncludesArrayOfStringsValue(
          countryIds,
          item.country,
        );

        const isStatusMatch =
          programStatus === "" ||
          item.statusValue?.toLowerCase() === programStatus?.toLowerCase();

        let hasMatchingProgramStartDate = true;

        if (programStartDate) {
          if (!item?.programStartDate) {
            hasMatchingProgramStartDate = false;
          } else {
            const selectedDateValue = dayjs(
              programStartDate,
              userCountryDateFormat,
            );
            const startOfSelectedDateMonthValue =
              selectedDateValue.startOf("month");
            const endOfSelectedDateMonthValue =
              selectedDateValue.endOf("month");

            const itemDate = dayjs(item?.programStartDate);

            hasMatchingProgramStartDate = isDateInRange(
              itemDate,
              startOfSelectedDateMonthValue,
              endOfSelectedDateMonthValue,
            );
          }
        }
        const hasMatchingProgramEndDate = compareDates(
          getValueOrBlank(programEndDate),
          getValueOrBlank(item?.programEndDate),
          getValueOrBlank(userCountryDateFormat),
        );

        return (
          programTemplateMatch &&
          isNextContactDateMatch &&
          isUpcomingSessionDateMatch &&
          isCoachNamesMatch &&
          isStatusMatch &&
          hasMatchingProgramStartDate &&
          hasMatchingProgramEndDate &&
          isCountryMatch &&
          participantNameMatch &&
          participantMobileNumberMatch &&
          participantEmailMatch &&
          projectManagerMatch
        );
      });
      setFilteredResultsData([...myData]);
      resetPagination();
    }
  }, [formValues]);

  const handleManageActionClick = (row?: CPParticipantsResultRowValue) => {
    dispatch(updateProgramCreatedDate(row?.createdDate));
    navigate(`${PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT}/${row?.id}`);
  };

  const handleAddActivityActionClick = (row: CPParticipantsResultRowValue) => {
    if (isEmpty(row.id)) return;
    dispatch(updateProgramCreatedDate(row?.createdDate));
    currentParticipantRef.current = row;
    setShowActivityModal(true);
  };

  const getAddActivityHeaderLabel = () => {
    const activityTitle = fetchLabel(
      activityTimelineSiteCoreKeys.activityTimeline,
      activityTimelineSiteCoreKeys.addActivityParticipantNamePopupHeader,
    );

    return replaceHashWithValue(
      activityTitle,
      getValueOrBlank(currentParticipantRef.current?.participantName),
    );
  };

  const handleAddActivityModalClose = () => setShowActivityModal(false);

  const columns = [
    {
      id: CPParticipantsResultFormKey.participantName,
      label: getLabel(participantNameLabel),
      isEmptyValue: true,
      isSortVisible: true,
      className: "cell-participant-name",
      render: (rowData: any) => {
        if (!rowData.participantName) return null;
        else {
          return hasModulePermission(MODULE_PERMISSIONS.COACH_PORTAL_VIEW) ? (
            <MuiLink
              href={`${PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT}/${rowData.id}`}
              isOpenNewTab
              id={`${pageId}-manage-participant`}
              data-testid="manage-participant-link"
            >
              <MuiBox data-testid="participant-name-link">
                <MuiEllipseLabel
                  data-testid="participant-name-link"
                  labelTypoClassName="Link participant-name-link"
                  text={rowData.participantName}
                  labelTypoVariant="h3"
                  placement="bottom-start"
                  pointerCursor
                />
              </MuiBox>
            </MuiLink>
          ) : (
            <MuiBox>
              <MuiTypography variant="h3" className="">
                {rowData.participantName}
              </MuiTypography>
            </MuiBox>
          );
        }
      },
    },
    {
      id: CPParticipantsResultFormKey.programTemplateName,
      label: getLabel(programTemplateLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: CPParticipantsResultFormKey.programStatus,
      label: getLabel(participantStatusLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <StatusBadge status={row.statusKey} label={row.statusValue} />
      ),
    },
    {
      id: CPParticipantsResultFormKey.nextContactDate,
      label: getLabel(nextContactDateLabel),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.nextContactDate)
              ? convertToUserFormat(row.nextContactDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CPParticipantsResultFormKey.upcomingSession,
      label: getLabel(upcomingSessionLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row?.upcomingSession)
              ? convertToUserFormatWithLocalTime(
                  row.upcomingSession,
                  getCustomDateFormatWithTime(userCountryDateFormat),
                )
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CPParticipantsResultFormKey.programEndDate,
      label: getLabel(programEndDateLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.programEndDate)
              ? convertToUserFormat(row.programEndDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CPParticipantsResultFormKey.coaches,
      label: getLabel(assignedCoachesLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) =>
        isNotEmpty(row.coachNamesArray) ? (
          <MuiBox className="cell-assigned-coaches">
            <MuiChipWithListView
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              data={row.coachNamesArray}
              headerLabel={getLabel(assignedCoachesLabel)}
              gridRef={React.createRef<HTMLElement>()}
              countToWrap={1}
              infoMessage={getInfoMessageForAssignedCoaches(
                row.coachNamesArray,
              )}
            />
          </MuiBox>
        ) : (
          EMPTY_VALUE_DASH
        ),
    },
    {
      id: CPParticipantsResultFormKey.participantEmail,
      label: getLabel(participantEmailLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: CPParticipantsResultFormKey.participantMobileNumber,
      label: getLabel(participantMobileNumberLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: CPParticipantsResultFormKey.projectManager,
      label: getLabel(projectManagerLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: CPParticipantsResultFormKey.countryName,
      label: getLabel(countryLabel),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-country",
    },
    {
      id: CPParticipantsResultFormKey.programStartDate,
      label: getLabel(programStartMonthYearLabel),
      isEmptyValue: true,
      isSortVisible: true,
      render: (row: any) => {
        const displayDateValue = isNotEmpty(row.programStartDate)
          ? dayjs(row.programStartDate).format(DATE_FORMAT.MMMM_YYYY)
          : EMPTY_VALUE_DASH;
        return (
          <MuiBox>
            <MuiTypography variant="h3">{displayDateValue}</MuiTypography>
          </MuiBox>
        );
      },
    },
    {
      id: "action",
      label: getLabel(actionLabel),
      render: (row: RowProps) => {
        const participantResultActions = [
          {
            permissionKey: MODULE_PERMISSIONS.COACH_PORTAL_EDIT,
            create: () => ({
              onClick: () =>
                handleManageActionClick(row as CPParticipantsResultRowValue),
              dataTestId: `${pageId}-manage`,
              label: getLabel(manageLabel),
              ariaLabel: getLabel(manageLabel),
            }),
          },
          {
            permissionKey: MODULE_PERMISSIONS.COACH_PORTAL_EDIT,
            create: () => ({
              onClick: () =>
                handleAddActivityActionClick(
                  row as CPParticipantsResultRowValue,
                ),
              dataTestId: `${pageId}-add-activity`,
              label: getLabel(addActivityLabel),
              ariaLabel: getLabel(addActivityLabel),
            }),
          },
        ];

        const participantResultMenuItems = buildActionMenuItems(
          row,
          participantResultActions,
          hasModulePermission,
        );

        return participantResultMenuItems ? (
          <MuiOptionMenu menuItems={participantResultMenuItems} />
        ) : null;
      },
    },
  ];

  const handleProgramStartDateChange = (
    key: string,
    newValue: dayjs.Dayjs | null,
  ) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(key, dateValue);
  };

  const tableHeaderForm = [
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.participantName}
      key={CPParticipantsResultFormKey.participantName}
    >
      <MuiTextField
        id={`${pageId}-${CPParticipantsResultFormKey.participantName}`}
        startAdornment={<TableHeaderSearchIcon />}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.programTemplateName}
      key={CPParticipantsResultFormKey.programTemplateName}
    >
      <MuiTextField
        id={`${pageId}-${CPParticipantsResultFormKey.programTemplateName}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.programStatus}
      key={CPParticipantsResultFormKey.programStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersValues.programStatus}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${CPParticipantsResultFormKey.programStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.nextContactDate}
      key={CPParticipantsResultFormKey.nextContactDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().nextContactDate !== ""
              ? dayjs(getValues().nextContactDate, userCountryDateFormat)
              : null
          }
          clearable
          format={userCountryDateFormat}
          valueFormat={userCountryDateFormat}
          onChange={(newValue) => {
            setValue(
              CPParticipantsResultFormKey.nextContactDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.upcomingSession}
      key={CPParticipantsResultFormKey.upcomingSession}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().upcomingSession !== ""
              ? dayjs(getValues().upcomingSession, userCountryDateFormat)
              : null
          }
          format={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              CPParticipantsResultFormKey.upcomingSession,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.programEndDate}
      key={CPParticipantsResultFormKey.programEndDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().programEndDate !== ""
              ? dayjs(getValues().programEndDate, userCountryDateFormat)
              : null
          }
          format={userCountryDateFormat}
          valueFormat={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              CPParticipantsResultFormKey.programEndDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.coaches}
      key={CPParticipantsResultFormKey.coaches}
    >
      <MuiTextField
        id={`${pageId}-${CPParticipantsResultFormKey.coaches}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.participantEmail}
      key={CPParticipantsResultFormKey.participantEmail}
    >
      <MuiTextField
        id={`${pageId}-${CPParticipantsResultFormKey.participantEmail}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.participantMobileNumber}
      key={CPParticipantsResultFormKey.participantMobileNumber}
    >
      <MuiTextField
        id={`${pageId}-${CPParticipantsResultFormKey.participantMobileNumber}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.projectManager}
      key={CPParticipantsResultFormKey.projectManager}
    >
      <MuiTextField
        id={`${pageId}-${CPParticipantsResultFormKey.projectManager}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.countryIds}
      key={CPParticipantsResultFormKey.countryIds}
    >
      <MuiMultiSelect
        label=""
        disabled={false}
        showSearch={true}
        showChip={false}
        textFieldWidth={0}
        options={uniqueCountriesData}
        id={`${pageId}-${CPParticipantsResultFormKey.countryIds}`}
        selectPlaceHolder={commonLabels.selectPlaceHolder}
        searchPlaceHolder={commonLabels.search}
        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
        doneText={commonLabels.done}
        allARIAProps={multiSelectARIALabels}
      />
    </FormController>,
    <FormController
      control={control}
      name={CPParticipantsResultFormKey.programStartDate}
      key={CPParticipantsResultFormKey.programStartDate}
    >
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={{
          fieldMonthPlaceholder: () => commonLabels.monthPlaceHolder,
          fieldYearPlaceholder: () => commonLabels.yearPlaceHolder,
        }}
      >
        <MuiDateField
          value={
            getValues().programStartDate !== ""
              ? dayjs(getValues().programStartDate, userCountryDateFormat)
              : null
          }
          format={DATE_FORMAT.MMM_YYYY_SLASH}
          clearable
          views={["month", "year"]}
          onChange={(newValue) =>
            handleProgramStartDateChange(
              CPParticipantsResultFormKey.programStartDate,
              newValue,
            )
          }
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,

    <></>,
  ];

  return (
    <CPParticipantsResultsWrapper>
      <MuiGrid container>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={!siteCoreLoading ? tableHeaderForm : []}
              columns={columns}
              tableResults={[...filteredResultsData]}
              noDataInApi={rows?.length === 0}
              emptyMessage={getLabel(emptyMessageKey)}
              noResultMessage={commonLabels.noResult}
              isShowDashIfValueEmpty
              priorityResults={[]}
              isLastColumnSticky
            />
          </MuiGrid>
        </MuiGrid>
        {showActivityModal && (
          <ParticipantAddActivity
            show={showActivityModal}
            handleCloseModal={handleAddActivityModalClose}
            pageId={pageId}
            programId={getValueOrBlank(currentParticipantRef.current?.id)}
            getListOfActivitiesApi={handleAddActivityModalClose}
            isEditActivity={false}
            customHeaderLabel={getAddActivityHeaderLabel()}
          />
        )}
      </MuiGrid>
    </CPParticipantsResultsWrapper>
  );
};
